
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { FileText, Info, AlertCircle } from "lucide-react";
import { generateGuaranteedUniqueAssessmentId, logAssessmentOperation } from "@/utils/assessmentUtils";

export interface CreateAssessmentDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  folderId: string;
  assessmentName?: string;
  assessmentDescription?: string;
  setAssessmentName?: (name: string) => void;
  setAssessmentDescription?: (description: string) => void;
  onSuccess?: () => void;
  isSharedFolder?: boolean;
  isEditing?: boolean;
  currentAssessmentId?: string;
  onSave?: (name: string, description?: string) => void;
}

const CreateAssessmentDialog = ({
  isOpen,
  onOpenChange,
  projectId,
  folderId,
  assessmentName: propAssessmentName = "",
  assessmentDescription: propAssessmentDescription = "",
  setAssessmentName,
  setAssessmentDescription,
  onSuccess,
  isSharedFolder,
  isEditing = false,
  currentAssessmentId,
  onSave
}: CreateAssessmentDialogProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState(propAssessmentName);
  const [description, setDescription] = useState(propAssessmentDescription);
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(projectId);
  const [projectName, setProjectName] = useState<string>("");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    description?: string;
  }>({});

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setValidationErrors({});
      setLoadingMessage("");
      
      logAssessmentOperation("CREATE_DIALOG_OPENED", { 
        projectId, 
        folderId, 
        isSharedFolder, 
        isEditing 
      });
      
      if (!isEditing) {
        setName("");
        setDescription("");
      } else {
        setName(propAssessmentName);
        setDescription(propAssessmentDescription);
      }
    }
  }, [isOpen, propAssessmentName, propAssessmentDescription, projectId, folderId, isSharedFolder, isEditing]);

  // Fetch project information when dialog opens
  useEffect(() => {
    const fetchProjectInfo = async () => {
      if ((!projectId || isSharedFolder) && folderId && isOpen) {
        try {
          setLoadingMessage("Hämtar projekt-information...");
          setError(null);
          
          logAssessmentOperation("FETCHING_PROJECT_ID", { folderId, isSharedFolder });
          
          const { data: folderData, error: folderError } = await supabase
            .from("folders")
            .select("project_id")
            .eq("id", folderId)
            .maybeSingle();
            
          if (folderError) {
            console.error("Error fetching folder:", folderError);
            throw new Error(`Kunde inte hitta mappinformation: ${folderError.message}`);
          }
          
          if (folderData?.project_id) {
            setResolvedProjectId(folderData.project_id);
            
            // Fetch project name
            const { data: projectData, error: projectError } = await supabase
              .from("projects")
              .select("name")
              .eq("id", folderData.project_id)
              .maybeSingle();
              
            if (projectError) {
              throw new Error(`Kunde inte hämta projektnamn: ${projectError.message}`);
            }
            
            if (projectData?.name) {
              setProjectName(projectData.name);
              // Set project name as placeholder suggestion for name field if not editing
              if (!isEditing && !name) {
                // Don't automatically set the name, just store it for placeholder
                setProjectName(projectData.name);
              }
            }
            
            setLoadingMessage("");
          } else {
            throw new Error("Mappen saknar projekt-ID");
          }
        } catch (error: any) {
          console.error("Error fetching project info:", error);
          setLoadingMessage("");
          setError(error.message || "Kunde inte hämta projekt-information");
        }
      } else if (projectId) {
        setResolvedProjectId(projectId);
        
        // Fetch project name for existing projectId
        const fetchProjectName = async () => {
          try {
            const { data: projectData, error: projectError } = await supabase
              .from("projects")
              .select("name")
              .eq("id", projectId)
              .maybeSingle();
              
            if (!projectError && projectData?.name) {
              setProjectName(projectData.name);
              // Don't automatically set the name, just store it for placeholder
            }
          } catch (error) {
            console.error("Error fetching project name:", error);
          }
        };
        
        fetchProjectName();
      }
    };
    
    fetchProjectInfo();
  }, [folderId, isSharedFolder, projectId, isOpen, isEditing, name]);

  // Enhanced validation function
  const validateForm = () => {
    const errors: { name?: string; description?: string } = {};
    
    // Name validation - always required
    if (!name.trim()) {
      errors.name = "Namn är obligatoriskt";
    } else if (name.trim().length < 3) {
      errors.name = "Namnet måste vara minst 3 tecken långt";
    }
    
    // Description validation - only required for new assessments
    if (!isEditing) {
      if (!description.trim()) {
        errors.description = "Beskrivning är obligatorisk";
      } else if (description.trim().length < 10) {
        errors.description = "Beskrivningen måste vara minst 10 tecken lång";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time validation on field changes
  useEffect(() => {
    if (name || description) {
      validateForm();
    }
  }, [name, description, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // If editing mode and onSave is provided, use that instead
    if (isEditing && onSave) {
      onSave(name.trim(), description.trim());
      onOpenChange(false);
      return;
    }
    
    const effectiveProjectId = resolvedProjectId || projectId;
    if (!effectiveProjectId) {
      setError("Kunde inte hitta projekt-ID. Kontrollera att mappen tillhör ett projekt.");
      return;
    }
    
    if (!folderId) {
      setError("Ingen mapp vald");
      return;
    }
    
    if (!name.trim()) {
      setError("Namn på risksammanställning krävs");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      logAssessmentOperation("CREATE_ASSESSMENT_START", {
        folder_id: folderId,
        project_id: effectiveProjectId,
        name: name.trim(),
        description: description.trim()
      });
      
      // Generate a guaranteed unique ID for the assessment
      const assessmentId = await generateGuaranteedUniqueAssessmentId(effectiveProjectId, folderId);
      
      logAssessmentOperation("ASSESSMENT_ID_GENERATED", { assessmentId });
      
      // Create risk assessment with user-provided name
      const assessmentData = {
        status: "utkast",
        svar: {},
        projektNamn: name.trim(), // Use user-provided name
        assessmentName: name.trim(), // Use user-provided name
        projektbeskrivning: description.trim(),
        assessmentId: assessmentId,
        metadata: {
          version: "1.0",
          created: new Date().toISOString(),
          projectId: effectiveProjectId,
          folderId: folderId,
          createdBy: (await supabase.auth.getUser()).data.user?.id
        }
      };

      const { data, error } = await supabase
        .from("folder_risk_assessments")
        .insert({
          folder_id: folderId,
          project_id: effectiveProjectId,
          name: name.trim(), // Use user-provided name for list display
          description: description.trim(),
          assessment_id: assessmentId,
          type: 'riskbedomning',
          data: assessmentData,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          updated_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select();
        
      if (error) {
        logAssessmentOperation("CREATE_ASSESSMENT_ERROR", { error: error.message });
        throw new Error(error.message);
      }

      logAssessmentOperation("CREATE_ASSESSMENT_SUCCESS", { 
        data: data?.[0],
        assessmentId,
        savedName: name.trim()
      });
      
      toast.success(`Risksammanställning "${name.trim()}" har skapats`);
      
      // Reset form
      setName("");
      setDescription("");
      onOpenChange(false);
      
      // Call success callback FIRST to update folder view
      if (onSuccess) {
        logAssessmentOperation("CALLING_SUCCESS_CALLBACK", {});
        onSuccess();
      }

      // Navigate with justCreated=true instead of createNew=true
      // This tells the system the assessment already exists but was just created
      setTimeout(() => {
        navigate(`/riskbedomning?projektId=${effectiveProjectId}&folderId=${folderId}&assessmentId=${assessmentId}&justCreated=true&steg=1`);
      }, 100);
      
    } catch (error: any) {
      console.error("Error creating assessment:", error);
      setError(error.message);
      toast.error(`Kunde inte skapa risksammanställning: ${error.message}`);
      logAssessmentOperation("CREATE_ASSESSMENT_FINAL_ERROR", { error: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      setName(propAssessmentName);
      setDescription(propAssessmentDescription);
    } else {
      setName("");
      setDescription("");
    }
    setValidationErrors({});
    onOpenChange(false);
  };

  const isFormValid = name.trim() && 
                     resolvedProjectId && 
                     folderId && 
                     !loadingMessage &&
                     Object.keys(validationErrors).length === 0 &&
                     (isEditing || description.trim());

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {isEditing ? "Redigera risksammanställning" : "Skapa ny risksammanställning"}
          </DialogTitle>
          <DialogDescription>
            {isEditing 
              ? "Uppdatera namn och beskrivning för din risksammanställning"
              : "Ge din risksammanställning ett tydligt namn och beskrivning"
            }
          </DialogDescription>
        </DialogHeader>
        
        {!isEditing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Tips för namngivning:</p>
                <ul className="text-xs space-y-1">
                  <li>• Använd beskrivande namn som "Villa Andersson - Grundläggning"</li>
                  <li>• Inkludera byggnadsdel eller arbetsmoment</li>
                  <li>• Gör det tydligt vad risksammanställningen omfattar</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assessment-name" className="text-sm font-medium">
              Namn på risksammanställning <span className="text-red-500">*</span>
            </Label>
            <Input
              id="assessment-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={projectName ? `T.ex. ${projectName} - Grundläggning` : "Ange ett beskrivande namn"}
              required
              className={`w-full ${validationErrors.name ? 'border-red-500' : ''}`}
              autoFocus
            />
            {validationErrors.name && (
              <div className="flex items-center gap-1 text-sm text-red-500">
                <AlertCircle className="h-3 w-3" />
                {validationErrors.name}
              </div>
            )}
            <p className="text-xs text-gray-600">
              Välj ett tydligt namn som beskriver vad denna risksammanställning omfattar
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="assessment-description" className="text-sm font-medium">
              Beskrivning {!isEditing && <span className="text-red-500">*</span>}
            </Label>
            <Textarea
              id="assessment-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beskriv kort vad denna risksammanställning handlar om, t.ex. vilka arbetsmoment, byggnadsdel eller fas som omfattas"
              rows={3}
              required={!isEditing}
              className={`w-full resize-none ${validationErrors.description ? 'border-red-500' : ''}`}
            />
            {validationErrors.description && (
              <div className="flex items-center gap-1 text-sm text-red-500">
                <AlertCircle className="h-3 w-3" />
                {validationErrors.description}
              </div>
            )}
            <p className="text-xs text-gray-600">
              {isEditing ? "Uppdatera beskrivningen (valfritt)" : "Förklara tydligt vad som ingår i denna riskbedömning"}
            </p>
          </div>
          
          {loadingMessage && (
            <div className="text-sm text-amber-500 flex items-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-amber-500 rounded-full border-t-transparent"></div>
              {loadingMessage}
            </div>
          )}
          
          {error && (
            <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded p-3">
              <p className="font-medium">Fel: {error}</p>
              <p className="mt-1">Kontrollera att mappen tillhör ett projekt och försök igen.</p>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Avbryt
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || !isFormValid}
              className="min-w-[140px]"
            >
              {isSubmitting ? "Sparar..." : (isEditing ? "Uppdatera" : "Skapa risksammanställning")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateAssessmentDialog;
